import { useEffect, useMemo, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { showWeekMetToast } from './WeekMetToast'
import { DURATION, WAVE_DELAY, WAVE_STEP, burstConfetti, pop, prefersReducedMotion } from './goalMotion'
import { TGoal } from './goalTypes'
import { TDayCell, TWeekRow, buildWeekRows, currentWeekDays, visibleWeekRows, weekSummary } from './weekHistoryRules'

// Week by week, newest first: this week as seven day pills, earlier weeks as one bar each (only this
// week carries per-unit times). When this week reaches its target the pills turn green in a wave,
// confetti bursts from the row and a toast says so.

/** Rows shown before "Show all": this week and the three before it. */
const COLLAPSED_ROWS = 4

type TMark = 'met' | 'active' | 'partial' | 'missed'

/** The 16px circle that leads each row: a green check, a half-filled circle, or a warning cross. */
const WeekMark = ({ mark, activeTone }: { mark: TMark; activeTone: string }) => {
    if (mark === 'met')
        return (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden className="shrink-0">
                <circle cx="8" cy="8" r="7" className="fill-success" />
                <path
                    d="M5 8.3L7 10.3L11 6"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        )
    if (mark === 'missed')
        return (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden className="shrink-0 text-warning">
                <circle cx="8" cy="8" r="6.4" stroke="currentColor" strokeWidth="1.3" />
                <path
                    d="M5.8 5.8l4.4 4.4M10.2 5.8l-4.4 4.4"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                />
            </svg>
        )
    return (
        <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden
            className={cn('shrink-0', mark === 'partial' ? 'text-gt-gold' : activeTone)}
        >
            <circle cx="8" cy="8" r="6.4" stroke="currentColor" strokeWidth="1.3" />
            <path d="M8 1.6a6.4 6.4 0 0 1 0 12.8V1.6Z" fill="currentColor" />
        </svg>
    )
}

const DayPill = ({ day, fill, waveDelay }: { day: TDayCell; fill: string; waveDelay?: number }) => {
    const ref = useRef<HTMLSpanElement>(null)
    const previous = useRef(day.state)
    // A day that fills pops once; so does every pill as the green wave passes over it.
    useEffect(() => {
        if (ref.current && day.state === 'done' && previous.current !== 'done') pop(ref.current)
        previous.current = day.state
    }, [day.state])
    useEffect(() => {
        if (ref.current && waveDelay !== undefined) pop(ref.current, waveDelay)
    }, [waveDelay])
    return (
        <span
            ref={ref}
            title={day.date}
            style={{
                transitionProperty: 'background-color, color, border-color',
                transitionDuration: `${DURATION.slow}ms`,
                transitionTimingFunction: 'ease-out',
                transitionDelay: `${waveDelay ?? 0}ms`,
            }}
            className={cn(
                'flex h-[18px] min-w-0 flex-1 items-center justify-center rounded-full text-[10px] font-semibold leading-3',
                day.state === 'done' && cn(fill, 'text-white'),
                day.state === 'none' && 'bg-secondary text-muted-foreground',
                day.state === 'future' && 'border border-dashed border-border bg-background text-muted-foreground',
                day.state === 'today' && 'border-[1.5px] border-foreground bg-background text-foreground'
            )}
        >
            {day.letter}
        </span>
    )
}

/** An earlier week, as one bar: full green when met, half gold when partial, empty when missed. */
const WeekBar = ({ outcome }: { outcome: TWeekRow['outcome'] }) => (
    <span className="flex h-[6px] min-w-0 flex-1 overflow-hidden rounded-full bg-secondary" aria-hidden>
        {outcome === 'complete' && <span className="h-full w-full rounded-full bg-success" />}
        {outcome === 'partial' && <span className="h-full w-1/2 rounded-full bg-gt-gold" />}
    </span>
)

const RowLabel = ({
    title,
    summary,
    tone,
    isCurrent,
}: {
    title: string
    summary: string
    tone: string
    isCurrent: boolean
}) => (
    <span className="flex w-[124px] shrink-0 flex-col whitespace-nowrap">
        <span className={cn('text-label-lg transition-colors duration-slow ease-out', tone)}>{title}</span>
        <span
            className={cn(
                'text-[11px] leading-[14px]',
                isCurrent ? 'text-muted-foreground' : 'text-muted-foreground/70'
            )}
        >
            {summary}
        </span>
    </span>
)

const CurrentWeekRow = ({ goal, row }: { goal: TGoal; row: TWeekRow }) => {
    const target = goal.weeklyTarget ?? 0
    const done = goal.thisWeek ?? 0
    const isMet = target > 0 && done >= target
    const days = useMemo(() => currentWeekDays(row.from, goal.thisWeekUnits ?? []), [row.from, goal.thisWeekUnits])
    const activeTone = goal.goalType === 'time' ? 'text-info' : 'text-gt-violet'
    const activeFill = goal.goalType === 'time' ? 'bg-info' : 'bg-gt-violet'

    // Reaching the target lands the new day in the week's colour first, then the week turns green in a
    // wave, confetti bursts and the toast appears. Only a change seen while open celebrates: opening a
    // goal whose week is already met just shows it met.
    const rowRef = useRef<HTMLDivElement>(null)
    const wasMet = useRef(isMet)
    const [shownMet, setShownMet] = useState(isMet)
    const [isCelebrating, setIsCelebrating] = useState(false)
    useEffect(() => {
        const justMet = isMet && !wasMet.current
        wasMet.current = isMet
        if (!justMet) {
            setShownMet(isMet)
            setIsCelebrating(false)
            return
        }
        showWeekMetToast(goal.title)
        if (prefersReducedMotion()) {
            setShownMet(true)
            return
        }
        const timers = [
            window.setTimeout(() => {
                setShownMet(true)
                setIsCelebrating(true)
            }, WAVE_DELAY),
            window.setTimeout(() => rowRef.current && burstConfetti(rowRef.current), WAVE_DELAY + 3 * WAVE_STEP),
            window.setTimeout(() => setIsCelebrating(false), WAVE_DELAY + 7 * WAVE_STEP + DURATION.slow),
        ]
        return () => timers.forEach((timer) => window.clearTimeout(timer))
        // Reacts to the week crossing its target, not to every refetch of the goal.
    }, [isMet])

    return (
        <div ref={rowRef} className="flex w-full items-center gap-2.5 border-b border-hairline py-2.5">
            <WeekMark mark={shownMet ? 'met' : 'active'} activeTone={activeTone} />
            <RowLabel
                title="This week"
                summary={weekSummary(row, done, target, goal.unit)}
                tone={shownMet ? 'text-success' : activeTone}
                isCurrent
            />
            <span className="flex min-w-0 flex-1 items-center gap-1">
                {days.map((day, index) => (
                    <DayPill
                        key={day.date}
                        day={day}
                        fill={shownMet ? 'bg-success' : activeFill}
                        waveDelay={isCelebrating ? index * WAVE_STEP : undefined}
                    />
                ))}
            </span>
        </div>
    )
}

const PAST_MARK: Record<TWeekRow['outcome'], TMark> = {
    complete: 'met',
    partial: 'partial',
    none: 'missed',
    future: 'missed',
}
const PAST_TONE: Record<TWeekRow['outcome'], string> = {
    complete: 'text-success',
    partial: 'text-gt-gold',
    none: 'text-warning',
    future: 'text-muted-foreground',
}

const PastWeekRow = ({ row }: { row: TWeekRow }) => (
    <div className="flex w-full items-center gap-2.5 border-b border-hairline py-2.5 last:border-b-0">
        <WeekMark mark={PAST_MARK[row.outcome]} activeTone="" />
        <RowLabel title={row.range} summary={weekSummary(row, 0, 0)} tone={PAST_TONE[row.outcome]} isCurrent={false} />
        <WeekBar outcome={row.outcome} />
    </div>
)

const WeekHistory = ({ goal }: { goal: TGoal }) => {
    const [showAll, setShowAll] = useState(false)
    const rows = useMemo(() => visibleWeekRows(buildWeekRows(goal.weeks ?? [], goal.asOf)), [goal.weeks, goal.asOf])
    if (rows.length === 0) return null
    const shown = showAll ? rows : rows.slice(0, COLLAPSED_ROWS)
    return (
        <div className="flex w-full flex-col">
            {shown.map((row) =>
                row.isCurrent ? (
                    <CurrentWeekRow key={row.label} goal={goal} row={row} />
                ) : (
                    <PastWeekRow key={row.label} row={row} />
                )
            )}
            {rows.length > COLLAPSED_ROWS && (
                <button
                    type="button"
                    aria-expanded={showAll}
                    onClick={() => setShowAll((value) => !value)}
                    className="self-start pt-2 text-label-md text-muted-foreground hover:text-foreground"
                >
                    {showAll ? 'Show fewer weeks' : `Show all ${rows.length} weeks`}
                </button>
            )}
        </div>
    )
}

export default WeekHistory
